import { Routes } from "@angular/router";
import { authGuard } from "./guards/auth.guard";
import {
  businessAdminGuard,
  businessContextGuard,
  businessScopeGuard,
  ownerScopeGuard,
  reportsGuard,
  superAdminGuard,
  taskHistoryGuard,
} from "./guards/role.guard";

export const routes: Routes = [
  {
    path: "sign-in",
    loadComponent: () =>
      import("./components/sign-in/sign-in.component").then(
        (m) => m.SignInComponent,
      ),
  },
  {
    path: "set-password",
    loadComponent: () =>
      import("./components/set-password/set-password.component").then(
        (m) => m.SetPasswordComponent,
      ),
  },
  {
    /**
     * The public QR scan page. No guard, by design — it is opened by a phone
     * camera on behalf of someone with no account at all, and the token in the
     * URL is the only credential.
     *
     * Must stay ABOVE the `**` wildcard below, which redirects everything
     * unmatched to /sign-in. It also has to be listed in `hideAppShell` in
     * app.component.ts, or an anonymous scanner gets the sidebar and the branch
     * context selector, which drive AccessService against a null user.
     */
    path: "scan/:token",
    loadComponent: () =>
      import("./components/scan/scan.component").then((m) => m.ScanComponent),
  },
  {
    /**
     * "/" is Home. Everything navigates to "/" after sign-in, and the role
     * guards bounce a refused user here too — this is the one place that
     * decides where "/" means.
     *
     * The dashboard that once lived here was a design comp with hardcoded
     * numbers and was deleted for it. Home is its replacement on one condition:
     * every number on it is a count over rows an existing endpoint returned.
     * See `models/home.model.ts`.
     */
    path: "",
    redirectTo: "/home",
    pathMatch: "full",
  },
  {
    path: "home",
    loadComponent: () =>
      import("./components/home/home.component").then((m) => m.HomeComponent),
    // authGuard only: every section inside decides for itself, by role, whether
    // it renders, so nobody signed in can be turned away from the landing page.
    canActivate: [authGuard],
  },
  {
    path: "checklists",
    loadComponent: () =>
      import("./components/checklists/checklist-board.component").then(
        (m) => m.ChecklistBoardComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: "reports",
    loadComponent: () =>
      import("./components/reports/reports.component").then(
        (m) => m.ReportsComponent,
      ),
    // The Compliance screen; the path predates the name. authGuard is listed
    // explicitly: unlike /setup/assignments this is a top-level route, with no
    // parent to inherit it from. reportsGuard admits any branch role, MANAGER
    // included — which branches each one sees is the Task API's narrowing.
    canActivate: [authGuard, businessContextGuard, reportsGuard],
  },
  {
    path: "task-history",
    loadComponent: () =>
      import("./components/task-history/task-history.component").then(
        (m) => m.TaskHistoryComponent,
      ),
    // Same line as /reports: this is the evidence behind that score, scoped by
    // the same API helper, so the two screens cannot disagree about branches.
    canActivate: [authGuard, businessContextGuard, taskHistoryGuard],
  },
  {
    path: "setup",
    loadComponent: () =>
      import("./components/setup/setup.component").then(
        (m) => m.SetupComponent,
      ),
    canActivate: [authGuard],
    /**
     * Every section of the app's administration, in one screen. Was split
     * across /setup and /settings, which were the same shell twice and made
     * "is adding a branch setup or settings?" a coin-flip.
     *
     * The children span four different guard levels, so nothing here can be
     * hoisted onto the parent without locking someone out of the rest.
     */
    children: [
      {
        // A component rather than `redirectTo`, because the landing section is
        // role-dependent and AccessService has not loaded yet when a static
        // redirect would fire.
        path: "",
        loadComponent: () =>
          import(
            "./components/setup/setup-redirect/setup-redirect.component"
          ).then((m) => m.SetupRedirectComponent),
      },
      {
        path: "businesses",
        loadComponent: () =>
          import("./components/setup/business-list/business-list.component").then(
            (m) => m.BusinessListComponent,
          ),
        canActivate: [superAdminGuard],
      },
      {
        path: "branches",
        loadComponent: () =>
          import("./components/setup/branch-list/branch-list.component").then(
            (m) => m.BranchListComponent,
          ),
        // Previously unguarded beyond the parent's authGuard, so a user with no
        // standing anywhere could deep-link to the page shell.
        canActivate: [businessScopeGuard],
      },
      {
        path: "members",
        loadComponent: () =>
          import("./components/setup/member-list/member-list.component").then(
            (m) => m.MemberListComponent,
          ),
        canActivate: [businessScopeGuard],
      },
      {
        // Branch staff without accounts, for the QR scan flow. Needs standing
        // somewhere; the page handles the no-branch-selected state itself,
        // because employees belong to a branch and there is no business-wide
        // view worth showing.
        path: "employees",
        loadComponent: () =>
          import(
            "./components/setup/employee-list/employee-list.component"
          ).then((m) => m.EmployeeListComponent),
        canActivate: [businessScopeGuard],
      },
      {
        path: "forms",
        loadComponent: () =>
          import("./components/forms/form-list/form-list.component").then(
            (m) => m.FormListComponent,
          ),
        // Templates live under a business, so FormService needs a businessId.
        // This guard is what guarantees one exists.
        canActivate: [businessContextGuard],
      },
      {
        path: "assignments",
        loadComponent: () =>
          import("./components/setup/assignments/assignment-list.component").then(
            (m) => m.AssignmentListComponent,
          ),
        // Was superAdminGuard, then businessScopeGuard when OWNER and MANAGER
        // gained scheduling. A MANAGER no longer schedules: an assignment sets
        // what a branch is measured on and what a miss costs, so it belongs to
        // whoever is accountable for the branch, not whoever works in it.
        canActivate: [businessContextGuard, ownerScopeGuard],
      },
      {
        // The document half of scheduling. Same gate as assignments, for the
        // same reason: a requirement sets what a branch is measured on and what
        // a miss costs, so it belongs to whoever is accountable for the branch
        // rather than whoever files the paperwork.
        path: "document-requirements",
        loadComponent: () =>
          import(
            "./components/setup/document-requirements/document-requirement-list.component"
          ).then((m) => m.DocumentRequirementListComponent),
        canActivate: [businessContextGuard, ownerScopeGuard],
      },
    ],
  },
  { path: "forms", redirectTo: "setup/forms", pathMatch: "full" },
  {
    path: "forms/new",
    loadComponent: () =>
      import("./components/forms/form-builder/form-builder.component").then(
        (m) => m.FormBuilderComponent,
      ),
    // Was superAdminGuard — a business authors its own templates now.
    canActivate: [authGuard, businessContextGuard, businessAdminGuard],
  },
  {
    path: "forms/:id/edit",
    loadComponent: () =>
      import("./components/forms/form-builder/form-builder.component").then(
        (m) => m.FormBuilderComponent,
      ),
    canActivate: [authGuard, businessContextGuard, businessAdminGuard],
  },
  {
    path: "forms/:id/preview",
    loadComponent: () =>
      import("./components/forms/form-preview/form-preview.component").then(
        (m) => m.FormPreviewComponent,
      ),
    // Reads a business-scoped document, so it needs a selected business.
    canActivate: [authGuard, businessContextGuard],
  },
  {
    path: "documents",
    loadComponent: () =>
      import("./components/documents/document-list/document-list.component").then(
        (m) => m.DocumentListComponent,
      ),
    // businessContextGuard joined authGuard when folders landed. The folder tree
    // lives at businesses/{bid}/documentFolders, so DocumentFolderService needs
    // a businessId — the same reason /setup/forms carries it. Note this is a
    // behaviour change: a user with no selected business used to reach an empty
    // Documents screen and is now redirected, which is the honest outcome for a
    // screen whose navigation is business-scoped.
    canActivate: [authGuard, businessContextGuard],
  },
  {
    // Deliberately top-level rather than a Setup child: this is
    // infrastructure, not business data, so it answers to the SuperAdmin claim
    // alone and not to any business scope.
    path: "admin/database",
    loadComponent: () =>
      import("./components/admin/dev-database/dev-database.component").then(
        (m) => m.DevDatabaseComponent,
      ),
    canActivate: [authGuard, superAdminGuard],
  },
  { path: "**", redirectTo: "/sign-in" },
];
